import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { PathFinder } from '../pathBuilder/PathFinder';
import { PathBuilder } from '../pathBuilder/PathBuilder';
import { PathReportPaths } from '../pathBuilder/PathReportPaths';
import { useGetPathReportStatesQuery } from '../services/edgeApi';
import { readLastState, LAST_STATE_KEY } from '../utils/pathReportState';
import { PathReportSelectionContext } from '../utils/pathReportState';
import {
  useGetWatchlistsQuery,
  useCreateWatchlistMutation,
  useDeleteWatchlistMutation,
} from '../services/edgeApi';
import { PathReportSidebar } from './PathReportSidebar';

export const PathReport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Always load states (for the dropdown)
  const { data, isLoading, isError, error } = useGetPathReportStatesQuery();
  const states = (data?.states as any[]) || [];

  // Seed from URL first, then localStorage
  const [selected, setSelected] = React.useState<{
    scenario_id?: string;
    gas_date?: string;
  }>(() => {
    const sp = new URLSearchParams(location.search);
    const sid = sp.get('scenario_id');
    const gd = sp.get('gas_date');
    if (sid && gd) return { scenario_id: sid, gas_date: gd };
    return readLastState() || {};
  });

  const selectorValue =
    selected?.scenario_id && selected?.gas_date
      ? `${selected.scenario_id}|${selected.gas_date}`
      : '';

  const applyState = React.useCallback(
    (scenario_id: string, gas_date: string, targetPath?: string) => {
      localStorage.setItem(
        LAST_STATE_KEY,
        JSON.stringify({ scenario_id, gas_date }),
      );
      setSelected({ scenario_id, gas_date });
      const sp = new URLSearchParams(location.search);
      sp.set('scenario_id', scenario_id);
      sp.set('gas_date', gas_date);
      const next = `${targetPath || location.pathname}?${sp.toString()}`;
      navigate(next, { replace: false });
    },
    [location.pathname, location.search, navigate],
  );

  // Keep local selected in sync when URL query changes externally
  React.useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const sid = sp.get('scenario_id');
    const gd = sp.get('gas_date');
    if (sid && gd) {
      setSelected((prev) =>
        prev.scenario_id === sid && prev.gas_date === gd
          ? prev
          : { scenario_id: sid, gas_date: gd },
      );
    }
  }, [location.search]);

  // New: ensure selected state exists; if not, clear localStorage and auto-pick first available
  React.useEffect(() => {
    if (isLoading) return;
    const exists =
      selected.scenario_id &&
      selected.gas_date &&
      states.some(
        (s) =>
          String(s.scenario_id) === String(selected.scenario_id) &&
          String(s.gas_date) === String(selected.gas_date),
      );

    if (!exists) {
      try {
        localStorage.removeItem(LAST_STATE_KEY);
      } catch {}
      if (states.length > 0) {
        const first = states[0];
        // Prefer keeping user on current route; update query params
        const sp = new URLSearchParams(location.search);
        sp.set('scenario_id', String(first.scenario_id));
        sp.set('gas_date', String(first.gas_date));
        setSelected({
          scenario_id: String(first.scenario_id),
          gas_date: String(first.gas_date),
        });
        navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
      } else {
        // No states available; clear selection to prevent downstream fetches
        setSelected({});
      }
    }
  }, [
    isLoading,
    states,
    selected.scenario_id,
    selected.gas_date,
    navigate,
    location.pathname,
    location.search,
  ]);

  const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [sid, gd] = e.target.value.split('|');
    if (sid && gd) applyState(sid, gd);
  };

  const loading = isLoading;
  const errorMsg = isError
    ? (error as any)?.data?.detail ||
      (error as any)?.error ||
      'Failed to load states'
    : null;

  return (
    <>
      {/* Global state selector (persists selection and refetches assets/report) */}
      <div className="mb-3">
        <select
          className="form-select"
          value={selectorValue}
          onChange={onSelectChange}
        >
          <option value="" disabled>
            {loading
              ? 'Loading…'
              : errorMsg
              ? 'Error loading scenarios'
              : 'Select a scenario'}
          </option>
          {states?.map((s) => (
            <option
              key={`${s.scenario_id}-${s.gas_date}`}
              value={`${s.scenario_id}|${s.gas_date}`}
            >
              Scenario {s.scenario_id} @ {s.gas_date}
            </option>
          ))}
        </select>
      </div>

      {/* Provide selection to nested routes and tabs */}
      <PathReportSelectionContext.Provider value={{ selected, setSelected }}>
        {/* Sidebar content for path modes renders here via nested routes */}
        {/* Sidebar content for path modes */}
        {location.pathname.includes('/paths/report') ? (
          <PathReportSidebar
            key={`sidebar-report-${selected.scenario_id}-${selected.gas_date}`}
          />
        ) : location.pathname.includes('/paths/finder') ? (
          <PathFinder
            key={`sidebar-finder-${selected.scenario_id}-${selected.gas_date}`}
            showControls={true}
            showTable={false}
            scenario_id={selected.scenario_id}
            gas_date={selected.gas_date}
          />
        ) : (
          <Outlet />
        )}
        <BelowMapSetter>
          <BelowMapTab
            key={`paths-${selected.scenario_id}-${selected.gas_date}`}
            contextKey="paths"
            tabName="Path Report"
            tabOrder={0}
            to="/paths/report"
            activeTab={location.pathname.includes('/paths/report')}
            unmountOnBlur
          >
            {location.pathname.includes('/paths/report') && (
              <PathReportPaths
              scenario_id={selected.scenario_id}
              gas_date={selected.gas_date}
              />
            )}
          </BelowMapTab>
          <BelowMapTab
            key={`pathfinder-${selected.scenario_id}-${selected.gas_date}`}
            contextKey="pathfinder"
            tabName="Path Finder"
            tabOrder={1}
            to="/paths/finder"
            activeTab={location.pathname.includes('/paths/finder')}
          >
            {location.pathname.includes('/paths/finder') && (
              <PathFinder
              showControls={false}
              showTable={true}
              scenario_id={selected.scenario_id}
              gas_date={selected.gas_date}
              />
            )}
          </BelowMapTab>
          <BelowMapTab
            key="pathBuilder"
            contextKey="pathBuilder"
            tabName="Path Builder"
            tabOrder={2}
            to="/paths/builder"
            activeTab={location.pathname.includes('/paths/builder')}
          >
            {location.pathname.includes('/paths/builder') && (
              <PathBuilder
              showControls={false}
              showTable={true}
              scenario_id={selected.scenario_id}
              gas_date={selected.gas_date}
              />
            )}
          </BelowMapTab>
          <BelowMapTab
            key="watchlists"
            contextKey="watchlists"
            tabName="Watchlists"
            tabOrder={3}
            activeTab={false}
          >
            <WatchlistTab />
          </BelowMapTab>
        </BelowMapSetter>
      </PathReportSelectionContext.Provider>
    </>
  );
};

const WatchlistTab: React.FC = () => {
  const { data: watchlists = [], isLoading } = useGetWatchlistsQuery();
  const [createWatchlist] = useCreateWatchlistMutation();
  const [deleteWatchlist] = useDeleteWatchlistMutation();
  const [newName, setNewName] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createWatchlist({ name: newName }).unwrap();
      setNewName('');
    } catch (err) {
      console.error('Error creating watchlist:', err);
    }
  };

  const handleDelete = async (wl: { id: number; name: string }) => {
    const ok = window.confirm(
      `Delete watchlist "${wl.name}"? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      setDeletingId(wl.id);
      await deleteWatchlist({ watchlistId: wl.id }).unwrap();
    } catch (err) {
      console.error('Error deleting watchlist:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const getCount = (wl: any): number | undefined => {
    if (Array.isArray(wl.paths)) return wl.paths.length;
    if (Array.isArray(wl.items)) return wl.items.length;
    if (Array.isArray(wl.node_ids)) return wl.node_ids.length;
    if (typeof wl.count === 'number') return wl.count;
    return undefined;
  };

  const formatDate = (value: any): string | null => {
    try {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString();
    } catch {
      return null;
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <input
          type="text"
          className="form-control"
          placeholder="New Watchlist"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ maxWidth: 240 }}
        />
        <button className="btn btn-primary" onClick={handleCreate}>
          Create
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2">
        {isLoading ? (
          <span>Loading watchlists...</span>
        ) : watchlists.length === 0 ? (
          <span>No watchlists yet.</span>
        ) : (
          watchlists.map((wl: any) => {
            const count = getCount(wl);
            const created = wl.created_at ? formatDate(wl.created_at) : null;

            return (
              <div
                key={wl.id}
                className="d-flex align-items-center justify-content-between border rounded p-2 gap-3"
                style={{ minWidth: 280 }}
              >
                <div className="d-flex flex-column">
                  <strong>{wl.name}</strong>
                  <small className="text-muted">
                    ID: {wl.id}
                    {typeof count === 'number' ? ` • items: ${count}` : ''}
                    {created ? ` • created: ${created}` : ''}
                  </small>
                </div>
                <button
                  className="btn btn-link btn-sm text-danger p-0"
                  title="Delete watchlist"
                  disabled={deletingId === wl.id}
                  onClick={() => handleDelete(wl)}
                >
                  Delete
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
